import type { IOptionsComposableVueQueryOptions } from '@/types/shared/vue-query'
import type { IModel } from '@/types/rc/miners'
import { getFanModels } from '@/services/core.service'
import { generateModelDisplayName, generateModelValueKey } from '@/util/rc'
import { useQuery } from '@tanstack/vue-query'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref } from 'vue'

/**
 * Composable for getFanModels response, mapped to ElSelect options.
 *
 * @param refetchOnWindowFocus default: true; useQuery refetch on window focus.
 * @param refetchOnMount default: true; useQuery refetch on mount.
 * @returns getPowerSupplyModels useQuery state.
 */
export const useFanModelOptions = (
  { refetchOnWindowFocus, refetchOnMount, enabled }: IOptionsComposableVueQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: ref(true),
  }
) => {
  const { isFetching, isRefetching, isError, data, error, refetch } = useQuery({
    queryKey: ['fan-model-options'],
    queryFn: async () => {
      const fanModels = await getFanModels()
      return fanModels.map((fanModel: IModel) => {
        return {
          label: generateModelDisplayName(fanModel.name!, fanModel.version),
          value: {
            ...fanModel,
            valueKey: generateModelValueKey(fanModel.name!, fanModel.version),
          },
        } as OptionType
      })
    },
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  })

  return {
    isFetching,
    isRefetching,
    isError,
    data,
    error,
    refetch,
  }
}
